import { initializeApp, type FirebaseApp } from "firebase/app";
import {
  browserLocalPersistence,
  getAuth,
  GoogleAuthProvider,
  onAuthStateChanged,
  setPersistence,
  signInWithPopup,
  signOut,
  type Auth,
  type User
} from "firebase/auth";
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  onSnapshot,
  runTransaction,
  setDoc,
  writeBatch,
  type Firestore
} from "firebase/firestore";
import { defaultState } from "./defaults";
import type {
  AppState,
  Booking,
  CalendarStatus,
  FormSubmission,
  HostProfile,
  PublicHostDocument,
  PublicHostState
} from "./types";
import { dateValueMs, slugify, toIsoDateString } from "./utils";

const config = {
  apiKey: String(import.meta.env.VITE_FIREBASE_API_KEY || ""),
  authDomain: String(import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || ""),
  projectId: String(import.meta.env.VITE_FIREBASE_PROJECT_ID || ""),
  storageBucket: String(import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || ""),
  messagingSenderId: String(import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || ""),
  appId: String(import.meta.env.VITE_FIREBASE_APP_ID || "")
};

export const firebaseConfigured = Boolean(config.apiKey && config.authDomain && config.projectId && config.appId);

let app: FirebaseApp | null = null;
let auth: Auth | null = null;
let db: Firestore | null = null;

const bootstrapOwnerEmails = String(
  import.meta.env.VITE_SCHEDULER_OWNER_EMAILS ||
  "alhossain118@gmail.com,alamin@offscreentax.com"
)
  .split(",")
  .map((email) => email.trim().toLowerCase())
  .filter(Boolean);

const accessConfigCollection = "schedulerAccess";
const accessConfigDocument = "config";
const accessEmailCollection = "schedulerAccessEmails";

export interface SchedulerAccess {
  ownerUid: string;
  ownerEmail: string;
  currentEmail: string;
  isOwner: boolean;
}

interface SchedulerAccessConfigDocument {
  ownerUid: string;
  ownerEmail: string;
  createdAt: string;
  updatedAt: string;
}

interface SchedulerAccessEmailDocument {
  email: string;
  addedBy: string;
  createdAt: string;
}

export class SchedulerAccessDeniedError extends Error {
  constructor(email: string) {
    super(`${email || "This Google account"} is not authorized to use this scheduler.`);
    this.name = "SchedulerAccessDeniedError";
  }
}

function normalizeAccessEmail(value: string | null | undefined): string {
  return String(value || "").trim().toLowerCase();
}

function verifiedUserEmail(user: User): string {
  const email = normalizeAccessEmail(user.email);
  if (!email || !user.emailVerified) throw new SchedulerAccessDeniedError(email);
  return email;
}

if (firebaseConfigured) {
  app = initializeApp(config);
  auth = getAuth(app);
  db = getFirestore(app);
  void setPersistence(auth, browserLocalPersistence);
}

export function observeUser(callback: (user: User | null) => void): () => void {
  if (!auth) {
    callback(null);
    return () => undefined;
  }
  return onAuthStateChanged(auth, callback);
}

export async function signInWithGoogle(): Promise<User> {
  if (!auth) throw new Error("Firebase Authentication is not configured.");
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  const result = await signInWithPopup(auth, provider);
  return result.user;
}

export async function signOutUser(): Promise<void> {
  if (auth) await signOut(auth);
}

export function currentUser(): User | null {
  return auth?.currentUser ?? null;
}

export async function ensureSchedulerAccess(user: User): Promise<SchedulerAccess> {
  const firestore = requireDb();
  const email = verifiedUserEmail(user);
  const configRef = doc(firestore, accessConfigCollection, accessConfigDocument);
  const emailRef = doc(firestore, accessEmailCollection, email);
  let emailSnapshot = await getDoc(emailRef);

  if (!emailSnapshot.exists()) {
    // The bootstrap owner creates the access-control documents on the first
    // dashboard load. Firestore Rules—not this client-side check—enforce who
    // is allowed to bootstrap the installation.
    if (!bootstrapOwnerEmails.includes(email)) throw new SchedulerAccessDeniedError(email);

    const configSnapshot = await getDoc(configRef);
    const now = new Date().toISOString();
    const batch = writeBatch(firestore);

    if (!configSnapshot.exists()) {
      batch.set(configRef, {
        ownerUid: user.uid,
        ownerEmail: email,
        createdAt: now,
        updatedAt: now
      } satisfies SchedulerAccessConfigDocument);
    }

    batch.set(emailRef, {
      email,
      addedBy: user.uid,
      createdAt: now
    } satisfies SchedulerAccessEmailDocument, { merge: true });
    await batch.commit();
    emailSnapshot = await getDoc(emailRef);
  }

  if (!emailSnapshot.exists()) throw new SchedulerAccessDeniedError(email);

  const configSnapshot = await getDoc(configRef);
  if (!configSnapshot.exists()) throw new Error("Scheduler access control has not been initialized.");
  const configData = configSnapshot.data() as SchedulerAccessConfigDocument;

  return {
    ownerUid: String(configData.ownerUid || ""),
    ownerEmail: normalizeAccessEmail(configData.ownerEmail),
    currentEmail: email,
    isOwner: configData.ownerUid === user.uid
  };
}

export async function listAllowedSchedulerEmails(user: User): Promise<string[]> {
  verifiedUserEmail(user);
  const firestore = requireDb();
  const snapshot = await getDocs(collection(firestore, accessEmailCollection));
  return snapshot.docs
    .map((item) => normalizeAccessEmail((item.data() as Partial<SchedulerAccessEmailDocument>).email || item.id))
    .filter(Boolean)
    .sort((a, b) => a.localeCompare(b));
}

export async function addAllowedSchedulerEmail(user: User, value: string): Promise<string> {
  const firestore = requireDb();
  const email = normalizeAccessEmail(value);
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) throw new Error("Enter a valid email address.");
  await setDoc(doc(firestore, accessEmailCollection, email), {
    email,
    addedBy: user.uid,
    createdAt: new Date().toISOString()
  } satisfies SchedulerAccessEmailDocument, { merge: true });
  return email;
}

export async function removeAllowedSchedulerEmail(user: User, value: string): Promise<void> {
  const firestore = requireDb();
  const email = normalizeAccessEmail(value);
  const configSnapshot = await getDoc(doc(firestore, accessConfigCollection, accessConfigDocument));
  if (!configSnapshot.exists()) throw new Error("Scheduler access control is unavailable.");
  const configData = configSnapshot.data() as SchedulerAccessConfigDocument;
  if (email === normalizeAccessEmail(configData.ownerEmail)) throw new Error("The owner email cannot be removed.");
  await deleteDoc(doc(firestore, accessEmailCollection, email));
}

export function watchSchedulerAccess(user: User, callback: (allowed: boolean) => void): () => void {
  const firestore = requireDb();
  const email = verifiedUserEmail(user);
  return onSnapshot(
    doc(firestore, accessEmailCollection, email),
    (snapshot) => callback(snapshot.exists()),
    () => callback(false)
  );
}

function requireDb(): Firestore {
  if (!db) throw new Error("Cloud Firestore is not configured.");
  return db;
}

function profileFromUser(user: User, publicSlug: string): HostProfile {
  const now = new Date().toISOString();
  return {
    uid: user.uid,
    email: user.email || "",
    displayName: user.displayName || user.email?.split("@")[0] || "Scheduler host",
    photoURL: user.photoURL || "",
    publicSlug,
    createdAt: now,
    updatedAt: now
  };
}

function candidateSlug(user: User, attempt: number): string {
  const base = slugify(user.displayName || user.email?.split("@")[0] || "host");
  if (attempt === 0) return base;
  return `${base}-${user.uid.slice(0, 4 + Math.min(attempt, 4)).toLowerCase()}`;
}

function normalizeState(value?: Partial<AppState>): AppState {
  const state = structuredClone(defaultState);
  if (!value) return state;
  const eventTypes = (value.eventTypes ?? state.eventTypes).map((event) => ({
    ...event,
    intakeMode: event.intakeMode ?? "direct",
    intakeFormId: event.intakeFormId ?? "",
    externalFormUrl: event.externalFormUrl ?? "",
    externalFormPresentation: event.externalFormPresentation ?? "redirect"
  }));
  return {
    ...state,
    ...value,
    eventTypes,
    forms: value.forms ?? state.forms,
    bookings: value.bookings ?? [],
    submissions: value.submissions ?? [],
    branding: { ...state.branding, ...(value.branding ?? {}) },
    availability: { ...state.availability, ...(value.availability ?? {}) }
  };
}

function publicState(state: AppState): PublicHostState {
  return {
    eventTypes: state.eventTypes,
    forms: state.forms,
    availability: state.availability,
    branding: state.branding
  };
}

export async function ensureHostAccount(user: User): Promise<{ profile: HostProfile; state: AppState }> {
  const firestore = requireDb();
  const privateRef = doc(firestore, "hosts", user.uid, "settings", "default");
  const existing = await getDoc(privateRef);
  if (existing.exists()) {
    const data = existing.data() as { profile?: HostProfile; state?: Partial<AppState> };
    const profile = data.profile ?? profileFromUser(user, candidateSlug(user, 1));
    const state = normalizeState(data.state);
    state.bookings = [];
    state.submissions = [];
    return { profile, state };
  }

  for (let attempt = 0; attempt < 6; attempt += 1) {
    const slug = candidateSlug(user, attempt);
    const publicRef = doc(firestore, "publicHosts", slug);
    try {
      const result = await runTransaction(firestore, async (transaction) => {
        const publicSnapshot = await transaction.get(publicRef);
        if (publicSnapshot.exists() && publicSnapshot.data().ownerId !== user.uid) throw new Error("slug_taken");
        const profile = profileFromUser(user, slug);
        const state = structuredClone(defaultState);
        const publicDoc: PublicHostDocument = {
          ownerId: user.uid,
          publicSlug: slug,
          displayName: profile.displayName,
          state: publicState(state),
          updatedAt: new Date().toISOString()
        };
        transaction.set(privateRef, { profile, state: publicDoc.state, updatedAt: publicDoc.updatedAt });
        transaction.set(publicRef, publicDoc);
        return { profile, state: { ...state, bookings: [], submissions: [] } };
      });
      return result;
    } catch (error) {
      if ((error as Error).message !== "slug_taken") throw error;
    }
  }
  throw new Error("A unique public booking address could not be created.");
}

export async function saveHostAccount(profile: HostProfile, state: AppState, previousSlug?: string): Promise<HostProfile> {
  const firestore = requireDb();
  const user = currentUser();
  if (!user || user.uid !== profile.uid) throw new Error("You must be signed in to save settings.");

  const nextSlug = slugify(profile.publicSlug);
  const privateRef = doc(firestore, "hosts", user.uid, "settings", "default");
  const publicRef = doc(firestore, "publicHosts", nextSlug);
  const updatedProfile = { ...profile, publicSlug: nextSlug, updatedAt: new Date().toISOString() };
  const stateForPublic = publicState(state);

  await runTransaction(firestore, async (transaction) => {
    const claimed = await transaction.get(publicRef);
    if (claimed.exists() && claimed.data().ownerId !== user.uid) throw new Error("That public booking name is already in use.");
    transaction.set(privateRef, { profile: updatedProfile, state: stateForPublic, updatedAt: updatedProfile.updatedAt }, { merge: true });
    transaction.set(publicRef, {
      ownerId: user.uid,
      publicSlug: nextSlug,
      displayName: updatedProfile.displayName,
      state: stateForPublic,
      updatedAt: updatedProfile.updatedAt
    } satisfies PublicHostDocument);
  });

  if (previousSlug && previousSlug !== nextSlug) {
    const oldRef = doc(firestore, "publicHosts", previousSlug);
    const old = await getDoc(oldRef);
    if (old.exists() && old.data().ownerId === user.uid) await deleteDoc(oldRef);
  }

  return updatedProfile;
}

function bookingFromFirestore(id: string, value: Record<string, unknown>): Booking {
  return {
    id,
    ...value,
    startAt: toIsoDateString(value.startAt),
    endAt: toIsoDateString(value.endAt),
    createdAt: toIsoDateString(value.createdAt)
  } as Booking;
}

function submissionFromFirestore(id: string, value: Record<string, unknown>): FormSubmission {
  return {
    id,
    ...value,
    createdAt: toIsoDateString(value.createdAt),
    updatedAt: toIsoDateString(value.updatedAt)
  } as FormSubmission;
}

export async function loadHostBookings(uid: string): Promise<Booking[]> {
  const firestore = requireDb();
  const snapshot = await getDocs(collection(firestore, "hosts", uid, "bookings"));
  return snapshot.docs
    .map((item) => bookingFromFirestore(item.id, item.data()))
    .sort((a, b) => dateValueMs(b.startAt) - dateValueMs(a.startAt));
}

export function watchHostBookings(uid: string, callback: (bookings: Booking[]) => void): () => void {
  const firestore = requireDb();
  return onSnapshot(collection(firestore, "hosts", uid, "bookings"), (snapshot) => {
    const bookings = snapshot.docs
      .map((item) => bookingFromFirestore(item.id, item.data()))
      .sort((a, b) => dateValueMs(b.startAt) - dateValueMs(a.startAt));
    callback(bookings);
  }, (error) => console.warn("Booking listener stopped", error));
}

export async function loadHostSubmissions(uid: string): Promise<FormSubmission[]> {
  const firestore = requireDb();
  const snapshot = await getDocs(collection(firestore, "hosts", uid, "submissions"));
  return snapshot.docs
    .map((item) => submissionFromFirestore(item.id, item.data()))
    .sort((a, b) => dateValueMs(b.createdAt) - dateValueMs(a.createdAt));
}

export function watchHostSubmissions(uid: string, callback: (submissions: FormSubmission[]) => void): () => void {
  const firestore = requireDb();
  return onSnapshot(collection(firestore, "hosts", uid, "submissions"), (snapshot) => {
    const submissions = snapshot.docs
      .map((item) => submissionFromFirestore(item.id, item.data()))
      .sort((a, b) => dateValueMs(b.createdAt) - dateValueMs(a.createdAt));
    callback(submissions);
  }, (error) => console.warn("Submission listener stopped", error));
}

export async function loadCalendarStatus(uid: string): Promise<CalendarStatus> {
  const firestore = requireDb();
  const snapshot = await getDoc(doc(firestore, "hosts", uid, "system", "calendar"));
  if (!snapshot.exists()) return { connected: false, email: "" };
  return snapshot.data() as CalendarStatus;
}

export async function loadPublicHost(slug: string): Promise<PublicHostDocument | null> {
  const firestore = requireDb();
  const snapshot = await getDoc(doc(firestore, "publicHosts", slug));
  if (!snapshot.exists()) return null;
  const data = snapshot.data() as PublicHostDocument;
  return {
    ...data,
    state: {
      ...data.state,
      forms: data.state.forms ?? []
    }
  };
}

export async function createCalendarOAuthState(uid: string): Promise<string> {
  const firestore = requireDb();
  const state = crypto.randomUUID().replaceAll("-", "") + crypto.randomUUID().replaceAll("-", "");
  const now = Date.now();
  await setDoc(doc(firestore, "oauthStates", state), {
    ownerId: uid,
    createdAtMs: now,
    expiresAtMs: now + 10 * 60 * 1000,
    returnUrl: `${window.location.origin}/integrations?calendar=connected`
  });
  return state;
}

export async function seedLegacyState(profile: HostProfile, state: AppState): Promise<void> {
  const firestore = requireDb();
  const batch = writeBatch(firestore);
  const stateForPublic = publicState(state);
  batch.set(doc(firestore, "hosts", profile.uid, "settings", "default"), {
    profile,
    state: stateForPublic,
    updatedAt: new Date().toISOString()
  }, { merge: true });
  batch.set(doc(firestore, "publicHosts", profile.publicSlug), {
    ownerId: profile.uid,
    publicSlug: profile.publicSlug,
    displayName: profile.displayName,
    state: stateForPublic,
    updatedAt: new Date().toISOString()
  });
  await batch.commit();
}
